import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SpotCard from './components/SpotCard';
import SpotFilters from './components/SpotFilters';
import MapView from './components/MapView';

import { spotService } from '../../services/spotService';

const SpotGuide = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    region: 'all',
    difficulty: 'all',
    amenities: []
  });
  const [spots, setSpots] = useState([]);
  const [filteredSpots, setFilteredSpots] = useState([]);
  const [isNearbyMode, setIsNearbyMode] = useState(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);

  // Mock data for weather and visitors (these features not implemented yet)
  const mockWeather = {
    temperature: 28,
    windSpeed: 12,
    waveHeight: 0.3,
    humidity: 65,
    visibility: 10,
    condition: "Paddle için ideal koşullar"
  };


  const mockReviews = [
  {
    id: 1,
    userName: "Elif Şahin",
    userAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17caa9ce8-1763301418543.png",
    userAvatarAlt: "Profesyonel kadın portresi sarı saçlı gülümseyen Türk kadın pembe tişört ile",
    rating: 5,
    comment: "Harika bir spot! Su çok sakin ve manzara muhteşem.",
    date: new Date('2025-12-15'),
    visitDate: new Date('2025-12-10'),
    isVerified: true,
    helpful: 12,
    photos: []
  }];


  // Fetch spots from Supabase on component mount
  useEffect(() => {
    const fetchSpots = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const data = await spotService?.getAllSpots();

        setSpots(data);
        setFilteredSpots(data);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching spots:', err);
        setError(err?.message || 'Spotlar yüklenirken bir hata oluştu');
        setIsLoading(false);
      }
    };

    fetchSpots();
  }, []);

  // Apply filters when filters or spots change
  useEffect(() => {
    // Skip filtering if in nearby mode (already sorted by distance)
    if (isNearbyMode) {
      return;
    }

    let filtered = [...spots];

    // Search filter
    if (filters?.search) {
      filtered = filtered?.filter((spot) =>
      spot?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
      spot?.location?.toLowerCase()?.includes(filters?.search?.toLowerCase())
      );
    }

    // Region filter
    if (filters?.region !== 'all') {
      filtered = filtered?.filter((spot) => {
        const regionMap = {
          'marmara': ['İstanbul', 'İzmir'],
          'ege': ['İzmir', 'Muğla'],
          'akdeniz': ['Antalya', 'Muğla'],
          'karadeniz': [],
          'ic-anadolu': []
        };
        return spot?.location?.split(',')?.some((loc) =>
        regionMap?.[filters?.region]?.some((region) => loc?.includes(region))
        );
      });
    }

    // Difficulty filter - FIXED: now correctly checks mapped difficulty field
    if (filters?.difficulty !== 'all') {
      // Map Turkish difficulty to English for DB comparison
      const difficultyMap = {
        'Başlangıç': 'Beginner',
        'Orta': 'Intermediate',
        'İleri': 'Advanced'
      };
      const targetDifficulty = difficultyMap?.[filters?.difficulty] || filters?.difficulty;

      filtered = filtered?.filter((spot) =>
      spot?.difficultyLevel === targetDifficulty || spot?.difficulty === filters?.difficulty
      );
    }

    setFilteredSpots(filtered);
  }, [filters, spots, isNearbyMode]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      region: 'all',
      difficulty: 'all',
      amenities: []
    });
  };

  const handleSpotClick = (spot) => {
    setSelectedSpot(spot);
  };

  // Handle "Find Near Me" button click
  const handleFindNearMe = () => {
    setIsLoadingLocation(true);
    setLocationError(null);

    if (!navigator?.geolocation) {
      setLocationError('Tarayıcınız konum servislerini desteklemiyor.');
      setIsLoadingLocation(false);
      return;
    }

    navigator.geolocation?.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position?.coords;

          // Fetch nearby spots (50km radius)
          const nearbySpots = await spotService?.getNearby(latitude, longitude, 50000);

          setFilteredSpots(nearbySpots);
          setIsNearbyMode(true);
          setIsLoadingLocation(false);

          // Show success message
          if (nearbySpots?.length > 0) {
            alert(`Yakınınızda ${nearbySpots?.length} adet spot bulundu.`);
          } else {
            alert('Yakınınızda spot bulunamadı. Arama yarıçapını artırmayı deneyin.');
          }
        } catch (err) {
          console.error('Error fetching nearby spots:', err);
          setLocationError(err?.message || 'Yakındaki spotlar yüklenirken bir hata oluştu.');
          setIsLoadingLocation(false);
        }
      },
      (error) => {
        // Better error logging to see actual error structure
        console.error('Geolocation error:', {
          code: error?.code,
          message: error?.message,
          fullError: JSON.stringify(error)
        });

        let errorMessage = 'Konum alınamadı.';

        // Check if error exists and has a code property
        if (error && typeof error?.code === 'number') {
          if (error?.code === 1) {// PERMISSION_DENIED
            errorMessage = 'Konum izni verilmediği için yakındaki spotlar gösterilemiyor.';
          } else if (error?.code === 2) {// POSITION_UNAVAILABLE
            errorMessage = 'Konum bilgisi şu anda kullanılamıyor.';
          } else if (error?.code === 3) {// TIMEOUT
            errorMessage = 'Konum alma işlemi zaman aşımına uğradı.';
          }
        } else if (error && error?.message) {
          // Use error message if available
          errorMessage = error?.message;
        } else if (!error) {
          // Handle case where error is null/undefined
          errorMessage = 'Bilinmeyen bir konum hatası oluştu.';
        }

        setLocationError(errorMessage);
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: false,
        timeout: 30000,
        maximumAge: 300000
      }
    );
  };

  // Handle reset to show all spots
  const handleResetNearby = () => {
    setIsNearbyMode(false);
    setLocationError(null);
    setFilteredSpots(spots);
  };

  // Loading State
  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>Spot Rehberi - PaddleTurkiye</title>
          <meta name="description" content="Türkiye'deki en iyi SUP spotlarını keşfedin." />
        </Helmet>
        <Header />
        <div className="main-content bg-background min-h-screen">
          <div className="max-w-[1536px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            <div className="flex items-center justify-center min-h-[50vh]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Spotlar yükleniyor...</p>
              </div>
            </div>
          </div>
        </div>
      </>);

  }

  // Error State
  if (error) {
    return (
      <>
        <Helmet>
          <title>Spot Rehberi - PaddleTurkiye</title>
          <meta name="description" content="Türkiye'deki en iyi SUP spotlarını keşfedin." />
        </Helmet>
        <Header />
        <div className="main-content bg-background min-h-screen">
          <div className="max-w-[1536px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            <div className="flex items-center justify-center min-h-[50vh]">
              <div className="text-center max-w-md">
                <div className="bg-red-100 p-4 rounded-full inline-flex mb-4">
                  <Icon name="AlertTriangle" size={48} className="text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Spotlar Yüklenemedi</h2>
                <p className="text-muted-foreground mb-6">
                  {error}
                </p>
                <button
                  onClick={() => window.location?.reload()}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2 px-6 rounded-lg flex items-center justify-center gap-2 mx-auto transition-colors">

                  <Icon name="RefreshCw" size={18} />
                  Tekrar Dene
                </button>
              </div>
            </div>
          </div>
        </div>
      </>);

  }

  return (
    <>
      <Helmet>
        <title>Spot Rehberi - PaddleTurkiye</title>
        <meta name="description" content="Türkiye'deki en iyi SUP spotlarını keşfedin." />
      </Helmet>
      <Header />
      <div className="main-content bg-background min-h-screen">
        <div className="max-w-[1536px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          <div className="mb-6 md:mb-8 lg:mb-10">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                  Spot Rehberi
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Türkiye'nin en iyi paddle board spotlarını keşfedin
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'outline'}
                  size="sm"
                  iconName="Grid3x3"
                  onClick={() => setViewMode('grid')}>

                  Liste
                </Button>
                <Button
                  variant={viewMode === 'map' ? 'default' : 'outline'}
                  size="sm"
                  iconName="Map"
                  onClick={() => setViewMode('map')}>

                  Harita
                </Button>
              </div>
            </div>

            {/* Near Me Button and Status Messages */}
            <div className="mb-4 space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="default"
                  size="md"
                  iconName="MapPin"
                  onClick={handleFindNearMe}
                  disabled={isLoadingLocation}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium">

                  {isLoadingLocation ? 'Konum alınıyor...' : 'Yakınımdakileri Bul'}
                </Button>

                {isNearbyMode &&
                <Button
                  variant="outline"
                  size="md"
                  iconName="X"
                  onClick={handleResetNearby}>

                    Tüm Spotları Göster
                  </Button>
                }
              </div>

              {/* Location Error Message */}
              {locationError &&
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Konum Hatası</p>
                    <p className="text-sm text-red-700 mt-1">{locationError}</p>
                  </div>
                </div>
              }

              {/* Nearby Mode Active Message */}
              {isNearbyMode && !locationError &&
              <div className="flex items-start gap-3 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <Icon name="Navigation" size={20} className="text-indigo-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-indigo-800">Yakındaki Spotlar</p>
                    <p className="text-sm text-indigo-700 mt-1">
                      Konumunuza en yakın spotlar mesafeye göre sıralanmıştır (50 km yarıçap).
                    </p>
                  </div>
                </div>
              }
            </div>

            <div className="flex items-center gap-4 p-4 bg-primary/10 rounded-lg border border-primary/20">
              <Icon name="Info" size={20} className="text-primary flex-shrink-0" />
              <p className="text-sm text-foreground">
                <span className="font-medium">{filteredSpots?.length} spot</span> bulundu. {isNearbyMode ? 'Mesafeye göre sıralanmıştır.' : 'Filtreleri kullanarak arama yapabilirsiniz.'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 md:gap-8">
            <div className="lg:col-span-1 space-y-6">
              <SpotFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters} />
            </div>

            <div className="lg:col-span-3">
              {viewMode === 'grid' ?
              <>
                  {filteredSpots?.length > 0 ?
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 lg:gap-6 mb-6">
                      {filteredSpots?.map((spot) =>
                  <SpotCard
                    key={spot?.id}
                    spot={spot}
                    showDistance={isNearbyMode} />

                  )}
                    </div> :

                <div className="text-center py-12 md:py-16 lg:py-20">
                      <Icon name="MapPin" size={48} className="text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2">
                        {spots?.length === 0 ? 'Henüz spot eklenmedi' : 'Spot Bulunamadı'}
                      </h3>
                      <p className="text-sm md:text-base text-muted-foreground mb-6">
                        {spots?.length === 0 ?
                    'Veritabanında henüz kayıtlı spot bulunmuyor. Yönetici panelinden yeni spotlar eklenebilir.' :
                    isNearbyMode ?
                    'Yakınınızda spot bulunamadı. Arama yarıçapını artırmayı deneyin.' : 'Arama kriterlerinize uygun spot bulunamadı. Filtreleri değiştirmeyi deneyin.'}
                      </p>
                      {spots?.length > 0 && !isNearbyMode &&
                  <Button variant="outline" onClick={handleClearFilters}>
                          Filtreleri Temizle
                        </Button>
                  }
                      {isNearbyMode &&
                  <Button variant="outline" onClick={handleResetNearby}>
                          Tüm Spotları Göster
                        </Button>
                  }
                    </div>
                }
                </> :

              <div className="h-[600px] md:h-[700px] lg:h-[800px]">
                  <MapView
                  spots={filteredSpots}
                  onSpotClick={handleSpotClick}
                  selectedSpot={selectedSpot} />

                </div>
              }
            </div>
          </div>
        </div>
      </div>
    </>);

};

export default SpotGuide;